import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Loader2 } from 'lucide-react';

// Fix default marker icon paths (Webpack/CRA quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [-6.385, 106.758]; // Depok fallback
const DEFAULT_ZOOM = 15;

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      map.setView([lat, lng], Math.max(map.getZoom(), 16));
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * MiniOdpMap
 * Props:
 *   latitude, longitude: string|number (controlled)
 *   onChange({ lat, lng }): callback when marker moves (click or search)
 *   height: px (default 240)
 */
export default function MiniOdpMap({ latitude, longitude, onChange, height = 240 }) {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const hasCoord = Number.isFinite(lat) && Number.isFinite(lng);
  const center = hasCoord ? [lat, lng] : DEFAULT_CENTER;

  const markerRef = useRef(null);
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);

  const setLatLng = useCallback((la, lo) => {
    onChange?.({ lat: Number(la.toFixed(7)), lng: Number(lo.toFixed(7)) });
  }, [onChange]);

  const dragEnd = useCallback(() => {
    const m = markerRef.current;
    if (m) {
      const pos = m.getLatLng();
      setLatLng(pos.lat, pos.lng);
    }
  }, [setLatLng]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    setSearching(true);
    try {
      // Nominatim (OpenStreetMap) — free, no API key
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const la = parseFloat(data[0].lat);
        const lo = parseFloat(data[0].lon);
        if (Number.isFinite(la) && Number.isFinite(lo)) setLatLng(la, lo);
      }
    } catch (_) { /* silent */ }
    finally { setSearching(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLatLng(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-2">
      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="odp-map-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari alamat atau tempat…"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          className="h-9 px-3 rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm bg-white disabled:opacity-50"
          data-testid="odp-map-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cari'}
        </button>
        <button
          type="button"
          onClick={useMyLocation}
          className="h-9 px-2 rounded-md border border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-sm bg-white"
          title="Gunakan lokasi saya"
          data-testid="odp-map-mylocation-btn"
        >
          <MapPin className="w-4 h-4" />
        </button>
      </form>

      {/* Map */}
      <div style={{ height, width: '100%' }} className="rounded-md overflow-hidden border border-slate-200">
        <MapContainer center={center} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={setLatLng} />
          {hasCoord && (
            <>
              <Marker
                position={[lat, lng]}
                draggable
                ref={markerRef}
                eventHandlers={{ dragend: dragEnd }}
              />
              <Recenter lat={lat} lng={lng} />
            </>
          )}
        </MapContainer>
      </div>
      <div className="text-xs text-slate-500">
        Klik peta atau geser pin untuk mengatur koordinat. Gunakan tombol <MapPin className="inline w-3 h-3" /> untuk lokasi Anda saat ini.
      </div>
    </div>
  );
}
